import { supabase } from '@/integrations/supabase/client';
import { handleError } from '@/utils/handleError';
import type { TodoItem } from '@/types/todo';
import { translateOutsideReact } from '@/i18n/translateOutsideReact';
import type { AnswerCardInput, AnswerCardResult } from '@/types/card';
import { postEdgeFunctionAction } from './postEdgeFunctionAction';

/**
 * Advisory context sent with a redirect: which surface the consultant/admin acted from.
 * The server treats it as a hint and verifies it against the caller's real platform_role
 * before recording it (FIN-877) — it can never, on its own, change what is written.
 */
export interface RedirectOptions {
  crossCompanyView?: boolean;
}

export class TodoApiClient {
  static async listTodos(companyId: string): Promise<TodoItem[]> {
    try {
      const { data, error } = await supabase.functions.invoke('todo-api', {
        body: { action: 'listTodos', companyId },
      });

      if (error) throw error;

      return data.todos;
    } catch (error) {
      await handleError(error, translateOutsideReact('consultantErrors.fallback.failedToFetchTodos'), 'listTodos');
      throw error;
    }
  }

  /**
   * platform-admin-cross-client-todos.AC1 — sends NO companyId, mirroring
   * ReviewApiClient.getReviewItems(). Scope is resolved from the caller server-side; the
   * edge function refuses anyone who is not a platform admin.
   */
  static async listAllTodos(): Promise<TodoItem[]> {
    try {
      const { data, error } = await supabase.functions.invoke('todo-api', {
        body: { action: 'listAllTodos' },
      });

      if (error) throw error;

      return data.todos;
    } catch (error) {
      await handleError(error, translateOutsideReact('consultantErrors.fallback.failedToFetchTodosAcrossCompanies'), 'listAllTodos');
      throw error;
    }
  }

  static async redirectTodo(todoId: string, opts?: RedirectOptions): Promise<TodoItem> {
    try {
      const { data, error } = await supabase.functions.invoke('todo-api', {
        body: { action: 'redirectTodo', todoId, crossCompanyView: opts?.crossCompanyView },
      });

      if (error) throw error;

      return data.todo;
    } catch (error) {
      await handleError(error, translateOutsideReact('consultantErrors.fallback.failedToSendTodoToClient'), 'redirectTodo');
      throw error;
    }
  }

  static async redirectTodos(
    todoIds: string[],
    opts?: RedirectOptions,
  ): Promise<{ sentIds: string[]; failedIds: string[] }> {
    try {
      const { data, error } = await supabase.functions.invoke('todo-api', {
        body: { action: 'redirectTodos', todoIds, crossCompanyView: opts?.crossCompanyView },
      });

      if (error) throw error;

      return data;
    } catch (error) {
      await handleError(error, translateOutsideReact('consultantErrors.fallback.failedToSendTodosToClient'), 'redirectTodos');
      throw error;
    }
  }

  /**
   * FIN-1165 F3: answers an unknown-card classify todo and teaches the card. A failure keeps the
   * server's own message (EdgeFunctionFailure), so the dialog can show it.
   */
  static async answerCardTodo(input: AnswerCardInput): Promise<AnswerCardResult> {
    return postEdgeFunctionAction<AnswerCardResult>('todo-api', 'resolveTodo', { ...input });
  }
}
